import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MailCheck } from 'lucide-react';
import { authAPI } from '../services/api';
import { AuthShell } from './auth/AuthShell';
import { Input, Label } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { StatePanel } from '../components/ui/StatePanel';
import { Rise } from '../components/ui/motion';
import { toast } from 'react-toastify';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Handle timer tick
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSuccess(true);
      setCountdown(90); // Start 90s cooldown
      toast.success('Reset link sent to your email');
    } catch (err: any) {
      const status = err.response?.status;
      const message = err.response?.data?.message;

      if (status === 404) {
        toast.error('No account found with that email address.');
      } else if (status === 429) {
        // If it's a rate limit error, we can try to parse the remaining seconds
        const match = message?.match(/wait\s+(\d+)\s+seconds/);
        if (match && match[1]) {
          setCountdown(parseInt(match[1], 10));
          setSuccess(true);
        }
        toast.error(message || 'Please wait before requesting another email.');
      } else {
        toast.error(message || 'Failed to send reset link');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email and we'll send you a link to reset your password."
    >
      {success ? (
        <Rise as="div" index={2} className="reset-success-message">
          <StatePanel
            layout="plain"
            icon={<MailCheck className="lnr-icon" aria-hidden="true" />}
            description={
              <>
                <p>
                  We sent a password reset link to <b>{email}</b>.
                </p>
                <p>Please check your inbox and spam folder.</p>
              </>
            }
            action={
              <>
                <div
                  className="auth-cooldown"
                  style={{ '--pct': Math.min(1, countdown / 90) } as React.CSSProperties}
                >
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleSubmit}
                    disabled={countdown > 0 || loading}
                  >
                    {countdown > 0 ? `Resend email in ${countdown}s` : 'Resend link'}
                  </Button>
                  {countdown > 0 && (
                    <span className="auth-cooldown__track" aria-hidden="true">
                      <span className="auth-cooldown__fill" />
                    </span>
                  )}
                </div>
                <Link to="/login" className="lnr-btn lnr-btn--primary lnr-btn--lg login-btn">
                  Back to Login
                </Link>
              </>
            }
          />
        </Rise>
      ) : (
        <form onSubmit={handleSubmit} className="login-form">
          <Rise as="div" index={2} className="form-group">
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              autoComplete="email"
            />
          </Rise>

          <Rise as="div" index={3}>
            <Button
              type="submit"
              variant="default"
              size="lg"
              className="login-btn"
              disabled={loading}
            >
              {loading ? 'Sending link…' : 'Send Reset Link'}
            </Button>
          </Rise>

          <Rise as="div" index={4} className="login-footer-text">
            Remember your password? <Link to="/login">Log in</Link>
          </Rise>
        </form>
      )}
    </AuthShell>
  );
};

export default ForgotPassword;
